import { ChangeDetectionStrategy, Component, computed, effect, HostBinding, input, signal } from '@angular/core';
import { ChangeHistory } from 'ag-common-svc/lib/components/changes-history-grid/change-history.model';
import { ChangeLogsType } from '@ag-common-lib/lib/models/registration/change-logs.model';
import { getLogIndexes, isLogArray } from '../../../utils/normalize-log';
import { Role, RoleDescriptionMap } from '@ag-common-lib/lib';

@Component({
  selector: 'ag-shr-change-log-agent-portal-role',
  templateUrl: './change-log-agent-portal-role.component.html',
  styleUrls: ['./change-log-agent-portal-role.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeLogAgentPortalRoleComponent {
  @HostBinding('class') className = 'change-log-agent-portal-role';

  log = input.required<ChangeHistory>();

  readonly logIndexesSig = computed(() => {
    const log = this.log();
    return log ? getLogIndexes(Object.entries(log)) : [];
  });

  addedRolesSig = signal<string>('');
  removedRolesSig = signal<string>('');
  updatedRolesSig = signal<string>('');
  oldDataSig = signal<string>('');

  protected readonly isLogArray = isLogArray;
  protected readonly ChangeLogsType = ChangeLogsType;

  constructor() {
    effect(
      () => {
        const log = this.log();
        const logData = log?.log;
        const oldData = log?.oldData;
        const oldDataRoles = this.mapRolesToDescriptions(oldData);
        this.oldDataSig.set(oldDataRoles ?? '');

        if (!logData) {
          this.addedRolesSig.set('');
          this.removedRolesSig.set('');
          this.updatedRolesSig.set('');
          return;
        }

        // added data
        if (!oldDataRoles) {
          const addedRoles = typeof logData === 'string' ? this.mapRolesToDescriptions([logData]) : this.getUpdatedLogDataValues(logData);
          this.addedRolesSig.set(addedRoles);
          this.removedRolesSig.set('');
          this.updatedRolesSig.set('');
          return;
        }

        this.addedRolesSig.set('');

        // removed data
        const removedRoles = this.getRemovedLogDataValues(logData);
        if (removedRoles.length > 0) {
          this.removedRolesSig.set(removedRoles);
          this.updatedRolesSig.set('');
          return;
        }

        // updated data
        this.removedRolesSig.set('');
        const updatedRoles = this.getUpdatedLogDataValues(logData);
        this.updatedRolesSig.set(updatedRoles);
      },
      { allowSignalWrites: true },
    );
  }

  private getRemovedLogDataValues = logData => {
    const filteredRoles = Object.values(logData)
      .filter(item => Array.isArray(item) && item.length === 3 && item[1] === 0 && item[2] === 0)
      .map(item => item[0]);
    return this.mapRolesToDescriptions(filteredRoles);
  };

  private getUpdatedLogDataValues = logData => {
    const filteredRoles = Object.values(logData)
      .filter(item => Array.isArray(item) && item.length === 1)
      .flat();
    return this.mapRolesToDescriptions(filteredRoles);
  };

  private mapRolesToDescriptions = roles =>
    roles
      ?.map(role => RoleDescriptionMap.get(role) ?? role)
      .filter(Boolean)
      .join(', ');
}
